'use client';

import React from 'react';
import { useGetClientAuditLogsQuery } from '@/app/(main)/client-detail/hooks/useGetClientAuditLogsQuery';
import { BaseButton } from '@/app/components/buttons/BaseButton';
import { AuditLogItem, AuditLogSkeleton, AuditLogsEmptyState, AuditLogsError } from './components';

export interface AuditLogItemType {
  id: string;
  title: string;
  detail: string | null;
  type: string | null;
  created_by: string | null;
  created_at: string;
}

const EST_TIMEZONE = 'America/New_York';

export function formatAuditDate(iso: string): string {
  if (!iso) return '';

  const date = new Date(iso);
  if (isNaN(date.getTime())) return '';

  const formatted = date.toLocaleString('en-US', {
    timeZone: EST_TIMEZONE,
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });

  // toLocaleString returns e.g. "03/24/2026, 14:30:00" — reformat to "MM/DD/YYYY — HH:mm:ss"
  const [datePart, timePart] = formatted.split(', ');
  return `${datePart} — ${timePart} EST`;
}

function AuditLogsInitialSkeleton() {
  return (
    <ul className="space-y-0">
      {Array.from({ length: 6 }).map((_, i) => (
        <AuditLogSkeleton key={i} />
      ))}
    </ul>
  );
}

export function AuditLogs() {
  const { data, isLoading, isError, isFetchingNextPage, fetchNextPage, hasNextPage } = useGetClientAuditLogsQuery();

  const logs = data?.pages.flatMap((page) => page.data) ?? [];

  return (
    <section className="flex h-full min-h-0 flex-col">
      <header className="shrink-0 pt-[30px] flex flex-col gap-1">
        <h2 className="light text-blue-900">Audit logs</h2>
        <p className="text-body-regular text-grey-700">View recent updates to this client profile.</p>
      </header>

      {isLoading && (
        <div className="flex-1 min-h-0 overflow-hidden pr-2">
          <AuditLogsInitialSkeleton />
        </div>
      )}

      {!isLoading && isError && <AuditLogsError />}

      {!isLoading && !isError && !logs.length && (
        <div className="flex-1 min-h-0">
          <AuditLogsEmptyState />
        </div>
      )}

      {!isLoading && !isError && !!logs.length && (
        <>
          <div className="flex-1 min-h-0 mt-4">
            <div className="h-full overflow-y-auto pr-2">
              <ul className="flex flex-col gap-1">
                {logs.map((item) => {
                  return <AuditLogItem key={item.id} {...item} />;
                })}

                {isFetchingNextPage && Array.from({ length: 3 }).map((_, i) => <AuditLogSkeleton key={`next-${i}`} />)}
              </ul>
            </div>
          </div>

          {hasNextPage && (
            <div className="shrink-0 mt-4">
              {isFetchingNextPage ? (
                <div className="w-[180px] px-[22px] py-[9px] bg-[#6C757D] rounded-[6px] flex justify-center items-center">
                  <span className="text-[#F8F9FA] font-inter font-normal text-base leading-6">Loading...</span>
                </div>
              ) : (
                <BaseButton
                  intent="primary"
                  className="w-[180px] bg-[#0F3D8B] text-white font-inter font-normal text-base leading-6 rounded-[6px] px-[22px] py-[9px]"
                  onClick={() => fetchNextPage()}
                >
                  Load More
                </BaseButton>
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
}
